import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaSearch, 
  FaFilter, 
  FaTh, 
  FaList, 
  FaPrint,
  FaCogs,
  FaEye,
  FaCopy,
  FaInfoCircle,
  FaLayerGroup,
  FaIndustry,
  FaFileAlt,
  FaDollarSign,
  FaChartLine,
  FaLeaf,
  FaLock,
  FaUsers
} from 'react-icons/fa';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { useLanguage } from '../../contexts/LanguageContext'; // Utilisation de votre contexte

const Printing = () => {
  const { t, lang } = useLanguage(); // Récupération de la traduction et de la langue

  // Données locales traduites (puisque non présentes dans le contexte partagé)
  const getPrintingData = (l) => {
    const isFr = l === 'fr';
    return {
      categories: [
        {
          id: 'printers',
          name: isFr ? 'Imprimantes' : 'Printers',
          description: isFr ? 'Imprimantes laser monochrome et couleur pour tous besoins' : 'Monochrome and color laser printers for all needs',
          icon: FaPrint,
          color: '#0ea5e9'
        },
        {
          id: 'mfp',
          name: isFr ? 'Multifonctions' : 'Multifunctions',
          description: isFr ? 'Systèmes multifonctions tout-en-un pour bureaux modernes' : 'All-in-one multifunction systems for modern offices',
          icon: FaCopy,
          color: '#10b981'
        },
        {
          id: 'production',
          name: isFr ? 'Production' : 'Production',
          description: isFr ? "Solutions d'impression production haute performance" : 'High-performance production printing solutions',
          icon: FaIndustry,
          color: '#f59e0b'
        },
        {
          id: 'software',
          name: isFr ? 'Logiciels' : 'Software',
          description: isFr ? 'Solutions logicielles de gestion documentaire' : 'Document management software solutions',
          icon: FaFileAlt,
          color: '#8b5cf6'
        },
        {
          id: 'consumables',
          name: isFr ? 'Consommables' : 'Consumables',
          description: isFr ? 'Cartouches et consommables pour toutes les gammes Kyocera' : 'Cartridges and consumables for all Kyocera ranges',
          icon: FaLayerGroup,
          color: '#dc2626'
        }
      ],
      printingProducts: [
        {
          id: 1,
          name: "ECOSYS P2235dn",
          image: "/images/Printing/kyocera-p2235dn.png",
          category: "printers",
          description: isFr ? "Imprimante laser monochrome A4 de bureau pour usage professionnel quotidien" : "A4 monochrome laser desktop printer for daily professional use",
          keyUse: isFr ? "Bureaux PME, Impression quotidienne, Documents professionnels" : "SMB offices, Daily printing, Professional documents",
          options: isFr ? "Format A4, Monochrome, Recto-verso automatique, Réseau Ethernet" : "A4 Format, Monochrome, Automatic duplex, Ethernet"
        },
        {
          id: 2,
          name: "ECOSYS P2235dw",
          image: "/images/Printing/kyocera-p2235dn.png",
          category: "printers",
          description: isFr ? "Imprimante laser monochrome A4 avec connectivité WiFi" : "A4 monochrome laser printer with WiFi connectivity",
          keyUse: isFr ? "Bureaux flexibles, Télétravail, Impression mobile" : "Flexible offices, Telework, Mobile printing",
          options: isFr ? "Format A4, Monochrome, WiFi, Mobile Print" : "A4 Format, Monochrome, WiFi, Mobile Print"
        },
        {
            id: 3,
            name: "ECOSYS P4060dn",
            image: "/images/Printing/kyocera-p4060dn.png",
            category: "printers",
            description: isFr ? "Imprimante laser monochrome A3 pour gros volumes" : "A3 monochrome laser printer for large volumes",
            keyUse: isFr ? "Plans techniques, Documents A3, Volumes importants" : "Technical plans, A3 documents, High volumes",
            options: isFr ? "Format A3, Monochrome, Haute vitesse, Réseau" : "A3 Format, Monochrome, High speed, Network"
        },
        {
            id: 4,
            name: "ECOSYS P5026cdn",
            image: "/images/Printing/kyocera-p5026cdn.png",
            category: "printers",
            description: isFr ? "Imprimante laser couleur A4 pour documents marketing" : "A4 color laser printer for marketing documents",
            keyUse: isFr ? "Marketing, Présentations couleur, Communication" : "Marketing, Color presentations, Communication",
            options: isFr ? "Format A4, Couleur, Réseau, Recto-verso" : "A4 Format, Color, Network, Duplex"
        },
        {
            id: 5,
            name: "ECOSYS P8060cdn",
            image: "/images/Printing/kyocera-p8060cdn.png",
            category: "printers",
            description: isFr ? "Imprimante laser couleur A3 haute performance" : "High performance A3 color laser printer",
            keyUse: isFr ? "CAD, Plans couleur, Affiches, Marketing grand format" : "CAD, Color plans, Posters, Large format marketing",
            options: isFr ? "Format A3, Couleur, Haute vitesse, Finition professionnelle" : "A3 Format, Color, High speed, Professional finish"
        },
        {
            id: 6,
            name: "ECOSYS PA2100cwx",
            image: "/images/Printing/kyocera-p8060cdn.png",
            category: "printers",
            description: isFr ? "Imprimante couleur A4 compacte avec WiFi" : "Compact A4 color printer with WiFi",
            keyUse: isFr ? "Petits bureaux, Impression couleur occasionnelle" : "Small offices, Occasional color printing",
            options: isFr ? "Format A4, Couleur, WiFi, Compact" : "A4 Format, Color, WiFi, Compact"
        },
        {
            id: 7,
            name: "ECOSYS PA3500x",
            image: "/images/Printing/kyocera-pa3500x.png",
            category: "printers",
            description: isFr ? "Imprimante monochrome A4 haute vitesse" : "High speed A4 monochrome printer",
            keyUse: isFr ? "Impression intensive, Documents volumineux" : "Intensive printing, Voluminous documents",
            options: isFr ? "Format A4, Monochrome, 35 ppm, ECOSYS Technology" : "A4 Format, Monochrome, 35 ppm, ECOSYS Technology"
        },
        {
            id: 8,
            name: "ECOSYS PA4000cx",
            image: "/images/Printing/kyocera-pa4000cx.png",
            category: "printers",
            description: isFr ? "Imprimante couleur A4 pour environnements exigeants" : "A4 color printer for demanding environments",
            keyUse: isFr ? "Bureaux moyens, Impression couleur régulière" : "Medium offices, Regular color printing",
            options: isFr ? "Format A4, Couleur, 40 ppm, Sécurité avancée" : "A4 Format, Color, 40 ppm, Advanced security"
        },
        {
            id: 9,
            name: "TASKalfa PA4500ci",
            image: "/images/Printing/kyocera-pa4500ci.png",
            category: "printers",
            description: isFr ? "Imprimante couleur A4 professionnelle haut de gamme" : "Professional high-end A4 color printer",
            keyUse: isFr ? "Entreprises, Marketing, Communication visuelle" : "Enterprises, Marketing, Visual communication",
            options: isFr ? "Format A4, Couleur, 45 ppm, Interface tactile" : "A4 Format, Color, 45 ppm, Touch interface"
        },
        // MFP
        {
            id: 10,
            name: "ECOSYS M2135dn",
            image: "/images/Printing/kyocera-m2135dn.png",
            category: "mfp",
            description: isFr ? "Multifonction monochrome A4 compact avec fonctions essentielles" : "Compact A4 monochrome multifunction with essential functions",
            keyUse: isFr ? "Impression, Copie, Scan - Usage basique" : "Print, Copy, Scan - Basic use",
            options: isFr ? "Format A4, Monochrome, Copie, Scan, Compact" : "A4 Format, Monochrome, Copy, Scan, Compact"
        },
        {
            id: 11,
            name: "ECOSYS M2635dn",
            image: "/images/Printing/kyocera-m2635dn.png",
            category: "mfp",
            description: isFr ? "Multifonction monochrome A4 pour petits groupes de travail" : "A4 monochrome multifunction for small workgroups",
            keyUse: isFr ? "Bureaux PME, Impression, Copie, Scan" : "SMB offices, Print, Copy, Scan",
            options: isFr ? "Format A4, Monochrome, 35 ppm, Recto-verso" : "A4 Format, Monochrome, 35 ppm, Duplex"
        },
        {
            id: 12,
            name: "ECOSYS M3860idnf",
            image: "/images/Printing/kyocera-m3860idnf.png",
            category: "mfp",
            description: isFr ? "Multifonction monochrome A4 avec fax et finition" : "A4 monochrome multifunction with fax and finishing",
            keyUse: isFr ? "Bureaux moyens, Impression, Copie, Scan, Fax" : "Medium offices, Print, Copy, Scan, Fax",
            options: isFr ? "Format A4, Monochrome, Fax, Finition, 60 ppm" : "A4 Format, Monochrome, Fax, Finishing, 60 ppm"
        },
        {
            id: 13,
            name: "ECOSYS M4125idn",
            image: "/images/Printing/kyocera-m4125idn.png",
            category: "mfp",
            description: isFr ? "Multifonction monochrome A3 pour applications variées" : "A3 monochrome multifunction for varied applications",
            keyUse: isFr ? "Bureaux A3, Plans, Documents techniques" : "A3 offices, Plans, Technical documents",
            options: isFr ? "Format A3, Monochrome, 25 ppm, Interface couleur" : "A3 Format, Monochrome, 25 ppm, Color interface"
        },
        {
            id: 14,
            name: "ECOSYS M5526cdn",
            image: "/images/Printing/kyocera-m5526cdn.png",
            category: "mfp",
            description: isFr ? "Multifonction couleur A4 pour environnements collaboratifs" : "A4 color multifunction for collaborative environments",
            keyUse: isFr ? "Bureaux couleur, Marketing, Communication" : "Color offices, Marketing, Communication",
            options: isFr ? "Format A4, Couleur, 26 ppm, Scan couleur" : "A4 Format, Color, 26 ppm, Color scan"
        },
        {
            id: 15,
            name: "ECOSYS M8124cidn",
            image: "/images/Printing/kyocera-m8124cidn.png",
            category: "mfp",
            description: isFr ? "Multifonction couleur A3 haute performance" : "High performance A3 color multifunction",
            keyUse: isFr ? "Départements, Impression A3 couleur, Workflows" : "Departments, A3 color printing, Workflows",
            options: isFr ? "Format A3, Couleur, 24 ppm, Interface 10.1\"" : "A3 Format, Color, 24 ppm, 10.1\" Interface"
        },
        {
            id: 16,
            name: "TASKalfa 2020",
            image: "/images/Printing/kyocera-taskalfa2020.png",
            category: "mfp",
            description: isFr ? "Multifonction monochrome A3 économique et fiable" : "Economic and reliable A3 monochrome multifunction",
            keyUse: isFr ? "Bureaux A3, Usage intensif, Économie" : "A3 offices, Intensive use, Economy",
            options: isFr ? "Format A3, Monochrome, 20 ppm, ECOSYS Technology" : "A3 Format, Monochrome, 20 ppm, ECOSYS Technology"
        },
        // PRODUCTION
        {
            id: 17,
            name: "TASKalfa Pro 15000c",
            image: "/images/Printing/kyocera-pro15000c.png",
            category: "production",
            description: isFr ? "Imprimante jet d'encre couleur haute vitesse pour production" : "High-speed color inkjet printer for production",
            keyUse: isFr ? "Impression commerciale, Centres de reprographie, Gros volumes" : "Commercial printing, Reprographics centers, High volumes",
            options: isFr ? "Jet d'encre couleur, 150 ppm A4 Simplex, Qualité offset, Finition inline" : "Color inkjet, 150 ppm A4 Simplex, Offset quality, Inline finishing"
        },
        {
            id: 18,
            name: "TASKalfa Pro 15000cB",
            image: "/images/Printing/kyocera-pro15000cb.png",
            category: "production",
            description: isFr ? "Imprimante jet d'encre couleur haute vitesse avec options avancées" : "High-speed color inkjet printer with advanced options",
            keyUse: isFr ? "Production commerciale, Impression professionnelle" : "Commercial production, Professional printing",
            options: isFr ? "Jet d'encre couleur, 150 ppm A4 Simplex, Finition avancée, Automation" : "Color inkjet, 150 ppm A4 Simplex, Advanced finishing, Automation"
        },
        {
            id: 19,
            name: "TASKalfa Pro 55000c",
            image: "/images/Printing/kyocera-pro55000c.png",
            category: "production",
            description: isFr ? "Imprimante jet d'encre couleur production ultra-haute vitesse" : "Ultra-high speed production color inkjet printer",
            keyUse: isFr ? "Centres d'impression, Production industrielle" : "Print centers, Industrial production",
            options: isFr ? "Jet d'encre couleur, 150 ppm A4 Simplex, Production industrielle, Automation complète" : "Color inkjet, 150 ppm A4 Simplex, Industrial production, Full automation"
        },
        // SOFTWARE
        {
            id: 20,
            name: "Kyocera Cloud Print and Scan",
            image: "/images/Printing/software.png",
            category: "software",
            description: isFr ? "Solution cloud pour impression et numérisation à distance" : "Cloud solution for remote printing and scanning",
            keyUse: isFr ? "Télétravail, Impression cloud, Mobilité" : "Telework, Cloud printing, Mobility",
            options: isFr ? "Cloud native, Impression mobile, Scan vers cloud, Sécurisé" : "Cloud native, Mobile printing, Scan to cloud, Secure"
        },
        {
            id: 21,
            name: "Kyocera Fleet Services",
            image: "/images/Printing/software.png",
            category: "software",
            description: isFr ? "Plateforme cloud de gestion complète de flotte d'impression" : "Complete cloud-based printing fleet management platform",
            keyUse: isFr ? "Gestion de flotte, Optimisation, Analytics" : "Fleet management, Optimization, Analytics",
            options: isFr ? "Cloud-based, Analytics avancés, Monitoring, Optimisation automatique" : "Cloud-based, Advanced analytics, Monitoring, Automatic optimization"
        },
        {
            id: 22,
            name: "Kyocera Net Manager",
            image: "/images/Printing/software.png",
            category: "software",
            description: isFr ? "Logiciel de supervision et maintenance à distance" : "Remote supervision and maintenance software",
            keyUse: isFr ? "IT Management, Maintenance préventive, Monitoring" : "IT Management, Preventive maintenance, Monitoring",
            options: isFr ? "Supervision réseau, Alertes automatiques, Gestion consommables, Reporting" : "Network supervision, Automatic alerts, Consumables management, Reporting"
        },
        {
            id: 23,
            name: "Data Security Kit",
            image: "/images/Printing/software.png",
            category: "software",
            description: isFr ? "Kit de sécurité avancée pour protection des données" : "Advanced security kit for data protection",
            keyUse: isFr ? "Sécurité documentaire, Conformité, Protection données" : "Document security, Compliance, Data protection",
            options: isFr ? "Chiffrement, Authentification, Audit trails, Conformité RGPD" : "Encryption, Authentication, Audit trails, GDPR compliance"
        },
        {
            id: 24,
            name: "Kyocera Device Manager",
            image: "/images/Printing/software.png",
            category: "software",
            description: isFr ? "Solution de gestion centralisée des périphériques" : "Centralized device management solution",
            keyUse: isFr ? "Gestion IT, Configuration centralisée, Déploiement" : "IT Management, Centralized configuration, Deployment",
            options: isFr ? "Gestion centralisée, Configuration automatique, Déploiement masse" : "Centralized management, Automatic configuration, Mass deployment"
        },
        // CONSUMABLES
        {
            id: 25,
            name: "Toner CL-8100",
            image: "/images/Printing/imagremovebg-preview.png",
            category: "consumables",
            description: isFr ? "Cartouche de toner couleur pour imprimantes Kyocera" : "Color toner cartridge for Kyocera printers",
            keyUse: isFr ? "Remplacement toner, Maintenance impression couleur" : "Toner replacement, Color print maintenance",
            options: isFr ? "Toner couleur, Haute capacité, Compatible ECOSYS, Qualité originale" : "Color toner, High capacity, ECOSYS compatible, Original quality"
        },
        {
            id: 26,
            name: "Tambour DK-1248",
            image: "/images/Printing/kyocera-dk1248.png",
            category: "consumables",
            description: isFr ? "Unité de tambour longue durée pour imprimantes monochrome" : "Long-life drum unit for monochrome printers",
            keyUse: isFr ? "Maintenance périphérique, Qualité d'impression" : "Device maintenance, Print quality",
            options: isFr ? "Tambour longue durée, Compatible ECOSYS, Haute qualité" : "Long-life drum, ECOSYS compatible, High quality"
        },
        {
            id: 27,
            name: "Kit de Maintenance HM-8100",
            image: "/images/Printing/removebg-preview22.png",
            category: "consumables",
            description: isFr ? "Kit de maintenance complet pour imprimantes haute performance" : "Complete maintenance kit for high performance printers",
            keyUse: isFr ? "Maintenance préventive, Optimisation performance" : "Preventive maintenance, Performance optimization",
            options: isFr ? "Kit complet, Maintenance préventive, Longue durée de vie" : "Complete kit, Preventive maintenance, Long life span"
        },
        {
            id: 28,
            name: "Encre IK-7105 (Set CMYK)",
            image: "/images/Printing/removebg-preview.png",
            category: "consumables",
            description: isFr ? "Set d'encres couleur pour imprimantes jet d'encre production" : "Color ink set for production inkjet printers",
            keyUse: isFr ? "Impression production couleur, Qualité professionnelle" : "Production color printing, Professional quality",
            options: isFr ? "Set CMYK complet, Qualité production, Haute capacité" : "Full CMYK set, Production quality, High capacity"
        }
      ]
    };
  };

  const PRINTING_DATA = getPrintingData(lang);

  const [filteredProducts, setFilteredProducts] = useState(PRINTING_DATA.printingProducts);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const productsPerPage = 6;

  const { ref: advantagesRef } = useScrollAnimation();

  useEffect(() => {
    let filtered = [...PRINTING_DATA.printingProducts];

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.keyUse.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy, lang]); // Dépend de lang pour re-filtrer lors du switch

  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const getCategoryInfo = (categoryId) => {
    return PRINTING_DATA.categories.find(cat => cat.id === categoryId);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Section Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
        padding: '6rem 0 4rem 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Éléments décoratifs */}
        <div style={{
          position: 'absolute', top: '10%', right: '10%', width: '200px', height: '200px',
          borderRadius: '50%', background: 'linear-gradient(45deg, rgba(14, 165, 233, 0.2), rgba(14, 165, 233, 0.1))', filter: 'blur(40px)'
        }} />

        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="text-center text-lg-start">
              <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                <Badge bg="danger" className="mb-3" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  <FaPrint className="me-2" />
                  {t.printing.hero.badge}
                </Badge>
                
                <h1 style={{ fontSize: '3.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.5rem', lineHeight: '1.2' }} 
                    dangerouslySetInnerHTML={{ __html: t.printing.hero.title }} />
                
                <p style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', marginBottom: '2rem', lineHeight: '1.6', maxWidth: '600px' }}>
                  {t.printing.hero.subtitle}
                </p>
                
                <div className="d-flex flex-wrap gap-3">
                  <Button variant="danger" size="lg"
                    onClick={() => document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' })}
                    style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', border: 'none', boxShadow: '0 4px 16px rgba(14, 165, 233, 0.4)' }}>
                    <FaEye className="me-2" />
                    {t.printing.hero.btnExplore}
                  </Button>
                  <Button variant="outline-light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', borderWidth: '2px' }}>
                    <FaInfoCircle className="me-2" />
                    {t.printing.hero.btnGuide}
                  </Button>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={4} className="text-center">
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
                style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '20px', padding: '2rem', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <img src="/images/Printing/PrintingImage.jpg" alt="Printing" style={{ width: '200px', height: '180px', objectFit: 'contain', marginBottom: '1rem', borderRadius: '16px' }} />
                <h4 style={{ color: '#fff', marginBottom: '1rem' }}>{t.printing.hero.techTitle}</h4>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>{t.printing.hero.techDesc}</p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section Filtres et Produits */}
      <section id="solutions-section" className="py-5">
        <Container>
          <Row>
            <Col lg={3}>
              <div style={{ background: '#fff', borderRadius: '20px', padding: '1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'sticky', top: '2rem', marginBottom: '2rem' }}>
                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', display: 'block' }}>
                    <FaSearch className="me-2" /> {t.printing.sidebar.search}
                  </label>
                  <Form.Control type="text" placeholder={t.printing.sidebar.searchPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ borderRadius: '12px', border: '2px solid #e2e8f0', fontSize: '0.9rem', padding: '0.75rem 1rem' }} />
                </div>

                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', display: 'block' }}>
                    <FaFilter className="me-2" /> {t.printing.sidebar.category}
                  </label>
                  <div className="d-grid gap-2">
                    <Button variant={selectedCategory === 'all' ? 'primary' : 'outline-secondary'} onClick={() => handleCategoryChange('all')} size="sm" style={{ borderRadius: '10px', textAlign: 'left' }}>
                      {t.printing.sidebar.all}
                    </Button>
                    {PRINTING_DATA.categories.map(category => (
                      <Button key={category.id} variant={selectedCategory === category.id ? 'primary' : 'outline-secondary'} onClick={() => handleCategoryChange(category.id)} size="sm" style={{ borderRadius: '10px', textAlign: 'left' }}>
                        <category.icon className="me-2" /> {category.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', display: 'block' }}>
                    {t.printing.sidebar.display}
                  </label>
                  <div className="d-flex gap-2">
                    <Button variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'} onClick={() => setViewMode('grid')} size="sm" style={{ borderRadius: '10px', flex: 1 }}><FaTh /></Button>
                    <Button variant={viewMode === 'list' ? 'primary' : 'outline-secondary'} onClick={() => setViewMode('list')} size="sm" style={{ borderRadius: '10px', flex: 1 }}><FaList /></Button>
                  </div>
                </div>

                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #dee2e6' }}>
                  <p style={{ color: '#495057', margin: 0, fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>
                    <strong style={{ color: '#0ea5e9' }}>{filteredProducts.length}</strong> {t.printing.sidebar.found}
                  </p>
                  {totalPages > 1 && (
                    <p style={{ color: '#6c757d', margin: '0.25rem 0 0 0', fontSize: '0.75rem', textAlign: 'center' }}>
                      Page {currentPage} {t.printing.sidebar.pageOf} {totalPages}
                    </p>
                  )}
                </div>
              </div>
            </Col>

            <Col lg={9}>
              {viewMode === 'grid' ? (
                <Row className="g-4">
                  {paginatedProducts.map((product, index) => (
                    <Col md={6} xl={4} key={product.id}>
                      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} whileHover={{ y: -5 }} style={{ height: '100%' }}>
                        <Card style={{ height: '100%', borderRadius: '20px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                          <div style={{ height: '200px', background: `${getCategoryInfo(product.category)?.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <img src={product.image} alt={product.name} style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                            <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', flexDirection: 'column' }}>
                              <FaPrint size={60} color={getCategoryInfo(product.category)?.color} />
                            </div>
                            <Badge style={{ position: 'absolute', top: '1rem', right: '1rem', backgroundColor: getCategoryInfo(product.category)?.color, borderRadius: '25px', padding: '0.5rem 1rem' }}>
                              {getCategoryInfo(product.category)?.name}
                            </Badge>
                          </div>
                          <Card.Body style={{ padding: '1.5rem' }}>
                            <h5 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem', fontSize: '1.1rem' }}>{product.name}</h5>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>{product.description}</p>
                            <div className="mb-3">
                              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem', border: '1px solid #e2e8f0' }}>
                                <div className="text-center">
                                  <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{lang === 'fr' ? 'Usage clé' : 'Key Usage'}</div>
                                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>{product.keyUse}</div>
                                </div>
                              </div>
                            </div>
                            <div>
                              <h6 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4a5568', textTransform: 'uppercase' }}><FaCogs size={12} color="#0ea5e9" className="me-2" />Technologies</h6>
                              <div style={{ background: '#f1f5f9', borderRadius: '8px', padding: '0.75rem' }}>
                                {product.options.split(',').map((option, idx) => (
                                  <div key={idx} style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ width: '4px', height: '4px', backgroundColor: '#0ea5e9', borderRadius: '50%', marginRight: '0.5rem' }}></span>
                                    <span>{option.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="d-grid gap-4">
                  {paginatedProducts.map((product, index) => (
                    <motion.div key={product.id} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                      <Card style={{ borderRadius: '20px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <Row className="g-0">
                          <Col md={4}>
                            <div style={{ height: '250px', background: `${getCategoryInfo(product.category)?.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img src={product.image} alt={product.name} style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                            </div>
                          </Col>
                          <Col md={8}>
                            <Card.Body style={{ padding: '2rem' }}>
                              <h4 style={{ fontWeight: 700, color: '#1e293b' }}>{product.name}</h4>
                              <p style={{ color: '#64748b' }}>{product.description}</p>
                              <Row>
                                <Col sm={6}>
                                  <small style={{ color: '#64748b', textTransform: 'uppercase' }}>{lang === 'fr' ? 'Usage clé' : 'Key Usage'}</small>
                                  <div style={{ fontWeight: 600 }}>{product.keyUse}</div>
                                </Col>
                                <Col sm={6}>
                                  <small style={{ color: '#64748b', textTransform: 'uppercase' }}>Technologies</small>
                                  <div style={{ background: '#f8fafc', padding: '0.5rem' }}>
                                    {product.options.split(',').map((option, idx) => (
                                      <div key={idx} style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}>
                                        <span style={{ width: '3px', height: '3px', backgroundColor: '#0ea5e9', borderRadius: '50%', marginRight: '0.4rem' }}></span>
                                        {option.trim()}
                                      </div>
                                    ))}
                                  </div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Col>
                        </Row>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <div className="d-flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Button key={i + 1} variant={currentPage === i + 1 ? "primary" : "outline-primary"} onClick={() => setCurrentPage(i + 1)} style={{ borderRadius: '50%', width: '40px', height: '40px', fontWeight: 600 }}>
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {filteredProducts.length === 0 && (
                <div className="text-center py-5">
                  <FaPrint size={64} color="#64748b" />
                  <h4 style={{ color: '#64748b', marginTop: '1rem' }}>{t.printing.sidebar.noResults}</h4>
                  <p style={{ color: '#64748b' }}>{t.printing.sidebar.noResultsSub}</p>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section Avantages MDS */}
      <section ref={advantagesRef} className="advantages-section-printing" style={{ 
          padding: '8rem 0', background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%), url('/images/datacenter2.jpg')`,
          backgroundSize: 'cover', backgroundAttachment: 'fixed', position: 'relative', overflow: 'hidden'
      }}>
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row>
            <Col lg={12} className="text-center mb-5">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                <h2 style={{ fontSize: '2.8rem', fontWeight: 700, color: '#ffffff', marginBottom: '1.5rem' }} 
                    dangerouslySetInnerHTML={{ __html: t.printing.advantages.title }} />
                <p style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', maxWidth: '700px', margin: '0 auto' }}>
                  {t.printing.advantages.subtitle}
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row className="g-4">
            {/* Economic */}
            <Col lg={6}>
              <motion.div whileHover={{ y: -12, scale: 1.03 }} style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', height: '100%' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #dc2626, rgba(220,38,38,0.6))' }} />
                <div style={{ width: '100%', height: '120px', marginBottom: '2rem', borderRadius: '12px', overflow: 'hidden' }}>
                    <img src="/images/Printing/economic-benefits.jpg" alt="Economic" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                    <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', height: '100%' }}><FaDollarSign size={48} color="#dc2626" /></div>
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '1.25rem' }}>{t.printing.advantages.eco_title}</h3>
                <p style={{ color: '#4a5568', fontSize: '0.95rem' }}>{t.printing.advantages.eco_desc}</p>
                {t.printing.advantages.eco_list.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626', marginRight: '1rem', marginTop: '0.65rem' }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{item}</span>
                    </div>
                ))}
              </motion.div>
            </Col>

            {/* Optimization */}
            <Col lg={6}>
              <motion.div whileHover={{ y: -12, scale: 1.03 }} style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', height: '100%' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #dc2626, rgba(220,38,38,0.6))' }} />
                <div style={{ width: '100%', height: '120px', marginBottom: '2rem', borderRadius: '12px', overflow: 'hidden' }}>
                    <img src="/images/Printing/optimization-benefits.jpg" alt="Optimization" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                    <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', height: '100%' }}><FaChartLine size={48} color="#dc2626" /></div>
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '1.25rem' }}>{t.printing.advantages.opti_title}</h3>
                <p style={{ color: '#4a5568', fontSize: '0.95rem' }}>{t.printing.advantages.opti_desc}</p>
                {t.printing.advantages.opti_list.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626', marginRight: '1rem', marginTop: '0.65rem' }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{item}</span>
                    </div>
                ))}
              </motion.div>
            </Col>

            {/* Hassle-free */}
            <Col lg={6} className="mt-4">
              <motion.div whileHover={{ y: -12, scale: 1.03 }} style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', height: '100%' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #dc2626, rgba(220,38,38,0.6))' }} />
                <div style={{ width: '100%', height: '120px', marginBottom: '2rem', borderRadius: '12px', overflow: 'hidden' }}>
                    <img src="/images/Printing/hassle-free-benefits.jpg" alt="Service" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                    <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', height: '100%' }}><FaUsers size={48} color="#dc2626" /></div>
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '1.25rem' }}>{t.printing.advantages.service_title}</h3>
                <p style={{ color: '#4a5568', fontSize: '0.95rem' }}>{t.printing.advantages.service_desc}</p>
                {t.printing.advantages.service_list.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626', marginRight: '1rem', marginTop: '0.65rem' }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{item}</span>
                    </div>
                ))}
              </motion.div>
            </Col>

            {/* Secure */}
            <Col lg={6} className="mt-4">
              <motion.div whileHover={{ y: -12, scale: 1.03 }} style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', height: '100%' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #dc2626, rgba(220,38,38,0.6))' }} />
                <div style={{ width: '100%', height: '120px', marginBottom: '2rem', borderRadius: '12px', overflow: 'hidden' }}>
                    <img src="/images/Printing/eco-security-benefits.jpg" alt="EcoSecure" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                    <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', height: '100%' }}><FaLeaf size={48} color="#dc2626" /></div>
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '1.25rem' }}>{t.printing.advantages.secure_title}</h3>
                <p style={{ color: '#4a5568', fontSize: '0.95rem' }}>{t.printing.advantages.secure_desc}</p>
                {t.printing.advantages.secure_list.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626', marginRight: '1rem', marginTop: '0.65rem' }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{item}</span>
                    </div>
                ))}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Printing;